/**
 * Treydorex (TRDX) sale — env-driven addresses, no Thirdweb.
 * Production builds require explicit env (no silent Sepolia contract fallbacks).
 */

import { requireEnvChainId, requireEnvString, SEPOLIA_CHAIN_ID } from "../../config/publicEnv";
import { resolveTrdxUsdcAddressFromEnv } from "../../config/trdxUsdcEnv";

/** @dev Sepolia test defaults — never used when NODE_ENV=production */
const DEV_TRDX_TOKEN_ADDRESS = "0x3417DC5bb83198dB740C27862E5Dba7AcED79947";
const DEV_TRDX_SALE_ADDRESS = "0xDC6D4B5D9f2BA5800325C6fdFe4E19365E1F52B5";
const DEV_SEPOLIA_USDC_ADDRESS = "0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238";

export const TRDX_CHAIN_ID = requireEnvChainId("REACT_APP_TRDX_CHAIN_ID", SEPOLIA_CHAIN_ID);

/** @type {string} */
export const TRDX_TOKEN_ADDRESS = requireEnvString(
  "REACT_APP_TRDX_TOKEN_ADDRESS",
  DEV_TRDX_TOKEN_ADDRESS
);

/** @type {string} */
export const TRDX_SALE_ADDRESS = requireEnvString(
  "REACT_APP_TRDX_SALE_ADDRESS",
  DEV_TRDX_SALE_ADDRESS
);

/**
 * USDC (6 decimals) for TRDX sale.
 * Set REACT_APP_TRDX_USDC_ADDRESS (preferred) or REACT_APP_MAINNET_USDC_ADDRESS on mainnet;
 * REACT_APP_SEPOLIA_USDC_ADDRESS remains supported for Sepolia dev / legacy builds.
 */
function resolveTrdxUsdcForBuild() {
  const fromEnv = resolveTrdxUsdcAddressFromEnv();
  if (fromEnv) return fromEnv;
  if (process.env.NODE_ENV === "production") {
    throw new Error(
      "Missing USDC address for TRDX sale. Set REACT_APP_TRDX_USDC_ADDRESS, REACT_APP_MAINNET_USDC_ADDRESS, or REACT_APP_SEPOLIA_USDC_ADDRESS."
    );
  }
  return DEV_SEPOLIA_USDC_ADDRESS;
}

export const TRDX_USDC_ADDRESS = resolveTrdxUsdcForBuild();

/** Minimal ERC-20 ABI for TRDX + USDC reads/writes used by the sale UI. */
export const TRDX_ERC20_MINIMAL_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
];

/** TreydorexSale.sol public interface used by the frontend. */
export const TREYDOREX_SALE_ABI = [
  "function buyWithETH(uint256 minTrdxOut) payable",
  "function buyWithUSDC(uint256 usdcAmount, uint256 minTrdxOut)",
  "function quoteETH(uint256 ethAmount) view returns (uint256)",
  "function quoteUSDC(uint256 usdcAmount) view returns (uint256)",
  "function tokensPerEth() view returns (uint256)",
  "function pricePerTrdxUsdc() view returns (uint256)",
  "function trdx() view returns (address)",
  "function usdc() view returns (address)",
  "function paused() view returns (bool)",
];
